import React from 'react';
import { motion } from 'framer-motion';
import CharacterDialogue from '../characters/CharacterDialogue';
import InsightBox from './InsightBox';
import ResearchBox from './ResearchBox';
import CompareBox from './CompareBox';
import WatchOutBox from './WatchOutBox';
import PracticeExercise from './PracticeExercise';
import ModuleSummary from './ModuleSummary';
import YourTurn from './YourTurn';
import ScienceNotes from './ScienceNotes';
import ToolLink from './ToolLink';
import OrnamentDivider from '../ornament/OrnamentDivider';

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const viewportOpts = { once: true, margin: '-40px' };

/**
 * Wrapper that adds scroll-anchor id and whileInView animation to each block.
 */
const Block = ({ index, children, className }) => (
  <motion.div
    id={`section-${index}`}
    variants={fadeIn}
    initial="hidden"
    whileInView="visible"
    viewport={viewportOpts}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * Renders module content blocks based on their type.
 * Each block is an object with { type, ...data }
 */
const ModuleContentRenderer = ({ blocks = [] }) => {
  return (
    <div className="space-y-6">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'narration':
            return (
              <Block key={index} index={index}>
                <div className="bg-courtyard-stone/5 border-l-4 border-courtyard-gold/30 rounded-r-xl px-4 sm:px-5 py-4">
                  <p className="text-courtyard-stone/80 italic leading-relaxed text-[15px]">
                    {block.text}
                  </p>
                </div>
              </Block>
            );

          case 'paragraph':
            return (
              <Block key={index} index={index}>
                <p className="text-courtyard-stone/80 leading-relaxed text-[15px]">
                  {block.text}
                </p>
              </Block>
            );

          case 'dialogue':
            return (
              <Block key={index} index={index}>
                <CharacterDialogue
                  characterId={block.character}
                  speaker={block.speaker}
                  text={block.text}
                  isNarration={false}
                />
              </Block>
            );

          case 'heading':
            return (
              <Block key={index} index={index} className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-8 bg-gradient-to-b from-courtyard-terracotta to-courtyard-gold rounded-full flex-shrink-0" />
                  <h3 className="font-display text-xl md:text-2xl font-bold text-courtyard-stone">
                    {block.text}
                  </h3>
                </div>
              </Block>
            );

          case 'subheading':
            return (
              <Block key={index} index={index}>
                <h4 className="font-display text-lg font-bold text-courtyard-stone/90 mt-4 mb-1">
                  {block.text}
                </h4>
              </Block>
            );

          case 'insight':
            return (
              <Block key={index} index={index}>
                <InsightBox title={block.title}>
                  {block.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
                  {block.text && <p>{block.text}</p>}
                </InsightBox>
              </Block>
            );

          case 'research':
            return (
              <Block key={index} index={index}>
                <ResearchBox title={block.title} source={block.source}>
                  {block.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
                  {block.text && <p>{block.text}</p>}
                </ResearchBox>
              </Block>
            );

          case 'compare':
            return (
              <Block key={index} index={index}>
                <CompareBox
                  title={block.title}
                  before={block.before}
                  after={block.after}
                  beforeLabel={block.beforeLabel}
                  afterLabel={block.afterLabel}
                  beforeItems={block.beforeItems}
                  afterItems={block.afterItems}
                />
              </Block>
            );

          case 'watchout':
            return (
              <Block key={index} index={index}>
                <WatchOutBox title={block.title}>
                  {block.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
                  {block.text && <p>{block.text}</p>}
                </WatchOutBox>
              </Block>
            );

          case 'exercise':
            return (
              <Block key={index} index={index}>
                <PracticeExercise
                  number={block.number}
                  title={block.title}
                  instructions={block.instructions}
                  prompts={block.prompts}
                />
              </Block>
            );

          case 'yourTurn':
            return (
              <Block key={index} index={index}>
                <YourTurn
                  id={block.id}
                  characterId={block.character}
                  speaker={block.speaker}
                  prompt={block.prompt}
                  options={block.options}
                />
              </Block>
            );

          case 'notes':
            return (
              <Block key={index} index={index}>
                <ScienceNotes title={block.title} intro={block.intro} items={block.items} />
              </Block>
            );

          case 'toolLink':
            return (
              <Block key={index} index={index}>
                <ToolLink toolId={block.toolId} title={block.title} text={block.text} />
              </Block>
            );

          case 'summary':
            return (
              <Block key={index} index={index}>
                <ModuleSummary
                  points={block.points}
                  characterId={block.characterId}
                  nextPreview={block.nextPreview}
                />
              </Block>
            );

          case 'list':
            return (
              <Block key={index} index={index}>
                <ul className="space-y-2 pl-1">
                  {(block.items || []).map((item, i) => (
                    <li key={i} className="flex gap-3 text-[15px] text-courtyard-stone/80">
                      <span className="text-courtyard-gold-ink mt-1.5 flex-shrink-0" aria-hidden="true">&#8226;</span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </Block>
            );

          case 'quote':
            return (
              <Block key={index} index={index}>
                <blockquote className="border-l-4 border-courtyard-gold/40 bg-courtyard-gold/5 rounded-r-xl px-4 sm:px-5 py-4 my-6 italic text-courtyard-stone/80 text-[15px] leading-relaxed">
                  &ldquo;{block.text}&rdquo;
                  {block.attribution && (
                    <footer className="mt-2 text-sm font-semibold text-courtyard-gold-ink not-italic">
                      {block.attribution}
                    </footer>
                  )}
                </blockquote>
              </Block>
            );

          case 'divider':
            return <OrnamentDivider key={index} id={`section-${index}`} />;

          default:
            return null;
        }
      })}
    </div>
  );
};

export default ModuleContentRenderer;
